import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { MODULES, MODULE_KEYS, type ModuleKey } from "@kipo/shared";
import { Icon, KipoAiSparkle, KipoMark, type IconName, MODULE_ICON } from "../ui/Icon";
import { Avatar, Badge, Button, Loading, cx, SIDE_PANEL_SLOT } from "../ui/kit";
import { moduleVars, useMe } from "../lib/me";
import { canOpen, firstOpenPath, moduleHome } from "../lib/access";
import { useTRPC } from "../lib/trpc";
import { useTodos } from "../lib/todos";
import { auth, getSession } from "../lib/session";
import { fmtDay, fullName } from "../lib/format";
import { CommandPalette } from "./CommandPalette";
import { ThemePicker } from "../ui/ThemePicker";

const ROLE_LABEL = { owner: "Titolare", admin: "Amministratore", manager: "Responsabile", user: "Utente", client: "Cliente" } as const;

/** Modulo corrente dal percorso: decide il colore dell'header. */
export function moduleFromPath(path: string): ModuleKey | null {
  const seg = path.split("/")[1];
  if (seg === "assistant") return "ai";
  return (MODULE_KEYS as readonly string[]).includes(seg ?? "") ? (seg as ModuleKey) : null;
}

function NavLink({ to, icon, iconColor, label, active, count, countTone, chevron, onClick }: { to: string; icon: IconName; iconColor?: string; label: string; active?: boolean; count?: number; countTone?: "module"; chevron?: "right" | "down"; onClick?: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cx("flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-sm", active ? "font-medium text-ink" : "text-text2 hover:bg-ground")}
      style={active ? { background: iconColor ? `color-mix(in srgb, ${iconColor} 9%, var(--color-surface))` : "var(--color-hover)" } : undefined}
    >
      <Icon name={icon} size={17} color={iconColor ?? "currentColor"} />
      <span className="truncate">{label}</span>
      {count !== undefined && count > 0 &&
        (countTone === "module" ? (
          <span className="ml-auto min-w-[18px] h-[18px] rounded-[9px] bg-projects text-white text-[11px] font-semibold flex items-center justify-center px-[5px]">{count}</span>
        ) : (
          <span className="ml-auto text-xs text-muted">{count}</span>
        ))}
      {chevron && <Icon name={chevron === "down" ? "chevron-down" : "chevron-right"} size={14} color="var(--color-subtle)" strokeWidth={2} style={{ marginLeft: count ? 6 : "auto" }} />}
    </Link>
  );
}

/** Kipo AI in cima alla barra: bordo nei colori dei moduli, stelline che brillano. */
function KipoAiLink({ active, onClick }: { active?: boolean; onClick?: () => void }) {
  return (
    <Link to="/assistant" onClick={onClick} aria-current={active ? "page" : undefined} className="kipo-ai relative overflow-hidden flex items-center gap-2.5 h-10 px-2.5 rounded-lg text-sm font-semibold text-ink">
      <KipoAiSparkle size={18} />
      <span>
        Kipo <span className="kipo-ai-text">AI</span>
      </span>
      <Icon name="chevron-right" size={14} color="var(--color-subtle)" strokeWidth={2} style={{ marginLeft: "auto" }} />
    </Link>
  );
}

function SubLink({ to, label, active, dot, muted, onClick }: { to: string; label: string; active?: boolean; dot?: string; muted?: boolean; onClick?: () => void }) {
  return (
    <Link to={to} onClick={onClick} aria-current={active ? "page" : undefined} className={cx("flex items-center gap-2 h-[30px] px-2 rounded-md text-[13px] min-w-0", active ? "font-medium bg-line-soft text-ink" : muted ? "text-muted hover:bg-ground" : "text-text2 hover:bg-ground")}>
      {dot && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />}
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const trpc = useTRPC();
  const me = useMe().data!;
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = moduleFromPath(path);
  const active = me.entitlement.activeModules;
  const projects = useQuery({ ...trpc.projects.list.queryOptions({ status: "ACTIVE" }), enabled: active.includes("projects") && current === "projects" });
  const todos = useTodos(me.role !== "client");
  const favs = useQuery(trpc.core.favorites.list.queryOptions());
  const projectId = path.match(/^\/projects\/([0-9a-f-]{36})/)?.[1];

  const moduleIcon = MODULE_ICON;
  const favColor: Record<string, string> = { projects: MODULES.projects.color, crm: MODULES.crm.color, sales: MODULES.sales.color, contracts: MODULES.contracts.color, expenses: MODULES.expenses.color, marketing: MODULES.marketing.color };

  return (
    <nav aria-label="Navigazione" className="h-full bg-surface border-r border-line flex flex-col w-64 overflow-y-auto">
      <div className="h-16 shrink-0 flex items-center gap-2.5 px-[18px] border-b border-line">
        <KipoMark />
        <span className="font-bold text-[19px] tracking-[-0.03em]">kipo</span>
        <TenantSwitch />
      </div>

      {active.includes("ai") && me.permissions.includes("ai:use") && (
        <div className="px-2.5 pt-3">
          <KipoAiLink active={path.startsWith("/assistant")} onClick={onNavigate} />
        </div>
      )}

      <div className="px-2.5 pt-2.5 pb-1 flex flex-col gap-0.5">
        <NavLink to="/" icon="home" label="Panoramica" active={path === "/"} onClick={onNavigate} />
        {me.role !== "client" && <NavLink to="/today" icon="check-list" label="Le mie attività" active={path === "/today"} count={todos.items.filter((t) => t.bucket === "late" || t.bucket === "today").length} onClick={onNavigate} />}
        <NavLink to="/notifications" icon="bell" label="Notifiche" active={path === "/notifications"} count={me.unreadNotifications} countTone="module" onClick={onNavigate} />
        {me.permissions.includes("documents:read") && <NavLink to="/documents" icon="folder" label="Documenti" active={path === "/documents"} onClick={onNavigate} />}
      </div>

      <div className="px-2.5 pt-2.5 pb-1 flex flex-col gap-0.5">
        <div className="text-[11px] font-semibold text-subtle px-2.5 pt-1 pb-1.5">Moduli</div>
        {(["projects", "crm", "sales", "contracts", "expenses", "marketing"] as const)
          .filter((k) => active.includes(k) && me.permissions.includes(MODULES[k].entryPermission))
          .map((k) => {
            const m = MODULES[k];
            const isCurrent = current === k;
            return (
              <div key={k} className="flex flex-col">
                <NavLink to={moduleHome(k, me)} icon={moduleIcon[k]} iconColor={m.color} label={m.name} active={isCurrent} chevron={isCurrent ? "down" : "right"} onClick={onNavigate} />
                <AnimatePresence initial={false}>
                  {isCurrent && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
                      <div className="flex flex-col gap-px mt-0.5 mb-1 ml-[18px] pl-2.5 border-l border-line">
                        {/* la panoramica si apre dal titolo: non la ripetiamo tra le voci */}
                        {m.navigation.filter((n) => n.to !== moduleHome(k, me) && canOpen(n.to, me, k)).map((n) => (
                          <SubLink key={n.key} to={n.to} label={n.label} active={path === n.to && !projectId} onClick={onNavigate} />
                        ))}
                        {k === "projects" &&
                          projects.data?.slice(0, 5).map((p) => <SubLink key={p.id} to={`/projects/${p.id}`} label={p.name} dot={p.color} active={projectId === p.id} onClick={onNavigate} />)}
                        {k === "projects" && (projects.data?.length ?? 0) > 5 && <SubLink to="/projects" label={`Altri ${projects.data!.length - 5} progetti`} muted onClick={onNavigate} />}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
      </div>

      {(favs.data?.length ?? 0) > 0 && (
        <div className="px-2.5 pt-2.5 pb-1 flex flex-col gap-0.5">
          <div className="text-[11px] font-semibold text-subtle px-2.5 pt-1 pb-1.5">Preferiti</div>
          {favs.data!.map((f) => (
            <Link key={f.id} to={(f.entityType === "project" ? `/projects/${f.entityId}` : f.entityType === "pipeline" ? "/crm" : `/${f.module}`) as string} onClick={onNavigate} className="flex items-center gap-2.5 h-8 px-2.5 rounded-lg text-[13px] text-text2 hover:bg-ground">
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: favColor[f.module] ?? "var(--color-ink)" }} />
              <span className="truncate">{f.label}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-auto border-t border-line px-2.5 py-2 flex flex-col gap-0.5">
        <NavLink to="/settings" icon="settings" label="Impostazioni" active={path.startsWith("/settings")} onClick={onNavigate} />
        {me.user.isSuperadmin && !me.impersonatedBy && <NavLink to="/platform" icon="shield" label="Console Kipo" active={path.startsWith("/platform")} onClick={onNavigate} />}
        <UserMenu />
      </div>
    </nav>
  );
}

/** Assistenza in corso: sempre visibile, con l'uscita a portata di mano. */
function ImpersonationBar({ name, tenant, admin }: { name: string; tenant: string; admin: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <div role="status" className="px-4 sm:px-7 py-2 bg-ink text-on-ink text-sm flex items-center gap-3 flex-wrap">
      <Icon name="shield" size={15} />
      <span>
        Stai usando Kipo come <strong>{name}</strong> in {tenant}. Ogni azione resta registrata a nome di {admin}.
      </span>
      <button
        type="button"
        disabled={busy}
        className="ml-auto h-7 px-3 rounded-md bg-surface text-ink text-[13px] font-semibold cursor-pointer disabled:opacity-60"
        onClick={async () => {
          setBusy(true);
          await auth.stopImpersonation().catch(() => {});
          location.assign("/platform");
        }}
      >
        Termina assistenza
      </button>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="flex-1 grid place-items-center p-8">
      <div className="max-w-sm text-center flex flex-col items-center gap-3">
        <span className="w-12 h-12 rounded-xl bg-surface border border-line grid place-items-center"><Icon name="lock" size={20} /></span>
        <h1 className="m-0 text-lg font-semibold">Questa sezione non è disponibile per il tuo ruolo</h1>
        <p className="m-0 text-sm text-muted">Se ti serve l'accesso, chiedilo a chi amministra lo spazio di lavoro.</p>
        <Link to="/" className="text-sm font-medium underline">Torna alla panoramica</Link>
      </div>
    </div>
  );
}

function TenantSwitch() {
  const s = getSession();
  const me = useMe().data!;
  const [open, setOpen] = useState(false);
  const many = (s?.tenants.length ?? 0) > 1;
  return (
    <div className="ml-auto relative min-w-0">
      <button type="button" onClick={() => many && setOpen((o) => !o)} className={cx("text-xs text-muted border border-line rounded-md px-2 py-0.5 max-w-[110px] truncate", many && "cursor-pointer hover:bg-ground")} aria-haspopup={many ? "menu" : undefined} title={me.tenant.name}>
        {me.tenant.name}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 z-30 w-56 rounded-xl border border-line bg-surface p-1 shadow-lg">
          {s!.tenants.map((t) => (
            <button key={t.id} role="menuitem" type="button" className="w-full text-left h-9 px-2.5 rounded-lg text-sm hover:bg-hover flex items-center justify-between cursor-pointer" onClick={async () => { setOpen(false); await auth.switchTenant(t.id); location.assign("/"); }}>
              <span className="truncate">{t.name}</span>
              {t.id === me.tenant.id && <Icon name="check" size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const me = useMe().data!;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 h-11 px-2.5 rounded-lg text-sm hover:bg-ground cursor-pointer text-left" aria-haspopup="menu" aria-expanded={open}>
        <span className="w-7 h-7 rounded-full bg-ink text-on-ink text-[11px] font-semibold flex items-center justify-center shrink-0">
          {(me.user.firstName[0] ?? "") + (me.user.lastName[0] ?? "")}
        </span>
        <span className="flex flex-col leading-tight min-w-0">
          <span className="font-medium truncate">{fullName(me.user)}</span>
          <span className="text-xs text-muted">{ROLE_LABEL[me.role]}</span>
        </span>
      </button>
      {open && (
        <div role="menu" className="absolute bottom-12 left-0 right-0 z-30 rounded-xl border border-line bg-surface p-1 shadow-lg">
          <button role="menuitem" type="button" className="w-full text-left h-9 px-2.5 rounded-lg text-sm hover:bg-hover flex items-center gap-2.5 cursor-pointer" onClick={() => { setOpen(false); navigate({ to: "/settings/profile" }); }}>
            <Icon name="user" size={15} /> Profilo
          </button>
          <div className="px-1.5 py-1.5 flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold text-subtle px-1">Tema</span>
            <ThemePicker compact />
          </div>
          <button role="menuitem" type="button" className="w-full text-left h-9 px-2.5 rounded-lg text-sm hover:bg-hover flex items-center gap-2.5 cursor-pointer" onClick={async () => { await auth.logout(); location.assign("/login"); }}>
            <Icon name="logout" size={15} /> Esci
          </button>
        </div>
      )}
    </div>
  );
}

/** Banner di stato dell'abbonamento (trial, scaduto, pagamento non riuscito). */
function EntitlementBanner() {
  const me = useMe().data!;
  const e = me.entitlement;
  if (me.deletionScheduledFor)
    return (
      <div className="flex items-center gap-3 px-4 sm:px-7 py-2 bg-[#B42318] text-white text-sm flex-wrap">
        <span>Lo spazio di lavoro verrà eliminato il {fmtDay(me.deletionScheduledFor)}.</span>
        {me.role === "owner" && (
          <Link to="/settings/workspace" className="underline underline-offset-4 decoration-white/60 hover:decoration-white">
            Annulla la cancellazione
          </Link>
        )}
      </div>
    );
  // prova e pagamenti li gestisce chi amministra lo spazio: agli altri il banner non serve
  if (me.role !== "owner" && me.role !== "admin") return null;
  if (e.status === "trialing" && e.trialEndsAt) {
    const days = Math.max(0, Math.ceil((new Date(e.trialEndsAt).getTime() - Date.now()) / 86_400_000));
    return (
      <div className="flex items-center gap-3 px-4 sm:px-7 py-2 bg-ink text-on-ink text-sm flex-wrap">
        <span>Prova gratuita: {days === 0 ? "scade oggi" : `ancora ${days} ${days === 1 ? "giorno" : "giorni"}`}.</span>
        <Link to="/settings/billing" className="underline underline-offset-4 decoration-white/50 hover:decoration-white">Attiva l'abbonamento</Link>
      </div>
    );
  }
  if (e.status === "past_due")
    return (
      <div className="px-4 sm:px-7 py-2 bg-warn-tint text-warn text-sm">
        L'ultimo pagamento non è andato a buon fine. <Link to="/settings/billing" className="underline font-medium">Aggiorna il metodo di pagamento</Link>
      </div>
    );
  return null;
}

function Expired() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md text-center flex flex-col gap-3 items-center">
        <KipoMark size={40} gap={3} radius={6} />
        <h1 className="m-0 text-3xl font-semibold tracking-tight">La prova è terminata</h1>
        <p className="m-0 text-muted">I tuoi dati sono al sicuro. Attiva l'abbonamento per continuare: 14 € al mese per utente, tutti i moduli inclusi.</p>
        <Link to="/settings/billing">
          <Button variant="primary" size="lg">
            Attiva l'abbonamento
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function AppShell() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const current = moduleFromPath(path);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (me.isLoading) return <Loading label="Apro Kipo" />;
  if (!me.data) return <Loading />;
  const blocked = !me.data.entitlement.usable && !path.startsWith("/settings");

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block sticky top-0 h-screen shrink-0">
        <Sidebar />
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div className="fixed inset-0 z-40 bg-scrim/30 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} />
            <motion.div className="fixed left-0 top-0 bottom-0 z-50 lg:hidden" initial={{ x: -264 }} animate={{ x: 0 }} exit={{ x: -264 }} transition={{ type: "spring", stiffness: 400, damping: 40 }}>
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <main className={cx("flex-1 min-w-0 flex flex-col", path.startsWith("/assistant") && "h-dvh overflow-hidden")} style={moduleVars(current)}>
        <div className="lg:hidden h-12 flex items-center gap-2 px-3 bg-surface border-b border-line">
          <button type="button" aria-label="Apri il menu" onClick={() => setMobileOpen(true)} className="h-9 w-9 rounded-lg hover:bg-hover flex items-center justify-center cursor-pointer">
            <Icon name="menu" size={18} />
          </button>
          <KipoMark size={20} />
          <span className="font-bold tracking-[-0.03em]">kipo</span>
          <button type="button" aria-label="Cerca" onClick={() => setPaletteOpen(true)} className="ml-auto h-9 w-9 rounded-lg hover:bg-hover flex items-center justify-center cursor-pointer">
            <Icon name="search" size={17} />
          </button>
        </div>
        {me.data.impersonatedBy && <ImpersonationBar name={fullName(me.data.user)} tenant={me.data.tenant.name} admin={me.data.impersonatedBy.email} />}
        <EntitlementBanner />
        {blocked ? (
          <Expired />
        ) : current && !canOpen(path, me.data, current) ? (
          // la radice del modulo porta alla prima pagina consentita; le altre dicono che non è accessibile
          path.replace(/\/$/, "") === `/${current}` && firstOpenPath(current, me.data) ? <Navigate to={firstOpenPath(current, me.data)!} replace /> : <NoAccess />
        ) : (
          <Outlet />
        )}
      </main>
      {/* qui si aggancia il pannello laterale dei dettagli (Drawer "docked"): sta accanto alla pagina, non sopra */}
      <div id={SIDE_PANEL_SLOT} className="hidden lg:block sticky top-0 h-screen shrink-0 z-30" style={moduleVars(current)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

export function ModuleBadge({ module, children }: { module: ModuleKey; children: ReactNode }) {
  return <Badge className="!bg-white/20 !text-white">{children}</Badge>;
}

export { Avatar };
